// -----------------------------------------------------------------------------
// Message composer — a mention-aware contenteditable editor (see
// `ChatComposerInput`) with auto-grow, Enter-to-send and Shift+Enter for a
// newline. Resets to empty whenever the active channel changes so half-typed
// drafts don't leak between conversations. The serialized body it emits carries
// inline @-mention tokens for users / parts / vehicles / orders / leads.
// -----------------------------------------------------------------------------

import { useRef, useState } from "react";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";
import { IChatChannel, IChatUser } from "../../models/Chat";
import ChatComposerInput, {
  ChatComposerInputHandle,
} from "../../components/chat/ChatComposerInput";

interface ComposerProps {
  channel: IChatChannel;
  usersById: Map<string, IChatUser>;
  meId: string;
  onSend: (body: string) => void;
}

export default function Composer({
  channel,
  usersById,
  meId,
  onSend,
}: ComposerProps) {
  const [value, setValue] = useState("");
  const inputRef = useRef<ChatComposerInputHandle>(null);

  const handleSubmit = () => {
    const body = (inputRef.current?.getValue() ?? "").trim();
    if (!body) return;
    onSend(body);
    inputRef.current?.clear();
    setValue("");
  };

  const placeholder =
    channel.kind === "channel"
      ? `Message #${channel.name}`
      : `Send a direct message`;

  return (
    <div className="border-t border-gray-200 bg-white p-3">
      <div className="rounded-lg border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
        <ChatComposerInput
          ref={inputRef}
          resetKey={channel.id}
          placeholder={placeholder}
          usersById={usersById}
          meId={meId}
          onChange={setValue}
          onSubmit={handleSubmit}
          className="px-3 py-2.5"
        />
        <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-2 py-1.5">
          <p className="text-[10px] text-gray-400">
            <kbd className="rounded border border-gray-200 bg-gray-50 px-1">
              @
            </kbd>{" "}
            to mention,{" "}
            <kbd className="rounded border border-gray-200 bg-gray-50 px-1">
              Enter
            </kbd>{" "}
            to send,{" "}
            <kbd className="rounded border border-gray-200 bg-gray-50 px-1">
              Shift+Enter
            </kbd>{" "}
            for a new line
          </p>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!value.trim()}
            className={classNames(
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold",
              value.trim()
                ? "bg-primary-600 text-white hover:bg-primary-500"
                : "bg-gray-100 text-gray-400"
            )}
          >
            <PaperAirplaneIcon className="h-3.5 w-3.5" />
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
